import jsPDF from 'jspdf'
import 'jspdf-autotable'
import moment from 'moment'
import addFont from '../../assets/fonts/fonts'
import { themeNumber } from './buildWeek'

/* Документ «Выписка из расписания по преподавателю» — порт кнопки «Печать»
   десктопной формы «Расписание преподавателя» (отчёт «Выписки по преподавателям»).

   Устройство таблицы то же, что в десктопе: строки — день недели и пара,
   колонки — учебные недели периода, сгруппированные по месяцам. Сетку по датам
   собирает клиент: сервер отдаёт занятия перечнем за весь период.

   Таблица собирается массивами, а не из скрытой таблицы на странице: служебная
   разметка не должна появляться в режиме даже на мгновение. */

const FONT = 'Montserrat'
const ORIENTATION = 'l'

// Размеры шрифта и полей подбираются под формат листа: то, что читается на A3,
// на A4 сливается, поэтому у форматов разные наборы, а не один на оба
const FORMATS = {
  A3: { format: 'a3', margin: 12, title: 15, sub: 12, info: 9.5, table: 6.5, padding: 1.2, dayWidth: 26, pairWidth: 24 },
  A4: { format: 'a4', margin: 8, title: 12, sub: 9.5, info: 7.5, table: 4.8, padding: 0.7, dayWidth: 19, pairWidth: 17 },
}

const DATE_FORMAT = 'DD.MM.YYYY'

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

const pairKey = lesson => (lesson.individual_work || lesson.pair_number == null
  ? `ind|${lesson.time_begin || ''}`
  : `p|${lesson.pair_number}`)

const pairLabel = lesson => {
  const name = lesson.individual_work || lesson.pair_number == null
    ? 'инд. раб.'
    : `${lesson.pair_number} пара`
  const time = lesson.time_begin && lesson.time_end
    ? `\n${lesson.time_begin}-${lesson.time_end}`
    : ''
  return name + time
}

// Содержимое ячейки — вид занятия, дисциплина, группа и аудитория одного занятия
const cellText = lesson => [
  lesson.lesson_type_name,
  lesson.course_name,
  lesson.group_name,
  lesson.room_name,
  themeNumber(lesson),
].filter(part => part !== null && part !== undefined && String(part).trim() !== '').join(' / ')

/* Сетка выписки: колонки — недели периода по возрастанию, строки — день недели
   и пара. Начало недели считается по дате занятия: в выдаче недели нет. */
const buildGrid = lessons => {
  const weeks = new Map()
  const days = new Map()

  lessons.forEach(lesson => {
    const date = moment(lesson.lesson_date, 'YYYY-MM-DD')
    const weekStart = date.clone().startOf('isoWeek')
    const weekId = weekStart.format('YYYY-MM-DD')
    if (!weeks.has(weekId)) {
      weeks.set(weekId, { id: weekId, start: weekStart, month: MONTHS[weekStart.month()] })
    }

    let day = days.get(lesson.weekday)
    if (!day) {
      day = { weekday: lesson.weekday, name: lesson.weekday_name, pairs: new Map() }
      days.set(lesson.weekday, day)
    }

    const key = pairKey(lesson)
    let pair = day.pairs.get(key)
    if (!pair) {
      pair = {
        key,
        label: pairLabel(lesson),
        individual: !!lesson.individual_work || lesson.pair_number == null,
        number: lesson.pair_number,
        time: lesson.time_begin || '',
        cells: new Map(),
      }
      day.pairs.set(key, pair)
    }

    const cell = pair.cells.get(weekId) || []
    cell.push(cellText(lesson))
    pair.cells.set(weekId, cell)
  })

  const columns = [...weeks.values()].sort((a, b) => a.start - b.start)
  const rows = [...days.values()]
    .sort((a, b) => a.weekday - b.weekday)
    .map(day => ({
      ...day,
      // индивидуальная работа — после всех пар дня, как и в расписании за неделю
      pairs: [...day.pairs.values()].sort((a, b) => (a.individual - b.individual)
        || ((a.number || 0) - (b.number || 0))
        || a.time.localeCompare(b.time)),
    }))

  return { columns, rows }
}

const buildHead = (columns, size) => {
  const monthSpans = []
  columns.forEach(column => {
    const last = monthSpans[monthSpans.length - 1]
    if (last && last.month === column.month) last.count += 1
    else monthSpans.push({ month: column.month, count: 1 })
  })

  return [
    [
      { content: 'День недели', rowSpan: 3, styles: { cellWidth: size.dayWidth } },
      { content: 'Занятие', rowSpan: 3, styles: { cellWidth: size.pairWidth } },
      ...monthSpans.map(span => ({ content: span.month, colSpan: span.count })),
    ],
    columns.map((column, index) => ({ content: String(index + 1) })),
    columns.map(column => ({ content: column.start.format(DATE_FORMAT) })),
  ]
}

const buildBody = (rows, columns) => {
  const body = []
  rows.forEach(day => {
    day.pairs.forEach((pair, index) => {
      const row = []
      if (!index) row.push({ content: day.name, rowSpan: day.pairs.length })
      row.push({ content: pair.label })
      columns.forEach(column => {
        row.push({ content: (pair.cells.get(column.id) || []).join('\n') })
      })
      body.push(row)
    })
  })
  return body
}

export const createStatement = ({ info, lessons }, sheetFormat) => {
  const size = FORMATS[sheetFormat] || FORMATS.A3

  addFont(jsPDF.API)
  const doc = new jsPDF({ putOnlyUsedFonts: true, orientation: ORIENTATION, format: size.format })

  const pageWidth = doc.internal.pageSize.getWidth()
  const center = pageWidth / 2
  const textWidth = pageWidth - size.margin * 2
  const { teacher, term, courses, plans, groups } = info

  const fio = [teacher.lastname, teacher.firstname, teacher.patronymic]
    .filter(Boolean).join(' ')

  doc.setFont(FONT, 'bold')
  doc.setFontSize(size.title)
  let y = size.margin + size.title * 0.4
  doc.text('Выписка из расписания по преподавателю', center, y, { align: 'center' })

  // наименование периода приходит с сервера полным, слово «года» после него не
  // дописывается — иначе получается «за 1 семестр 2026-2027 учебного года года»
  doc.setFontSize(size.sub)
  y += size.title * 0.55
  doc.text(`за ${term.name || ''}`, center, y, { align: 'center' })

  doc.setFont(FONT, 'normal')
  doc.setFontSize(size.info)
  y += size.sub * 0.75

  const line = (label, value) => {
    const text = `${label}: ${value || '—'}`
    const parts = doc.splitTextToSize(text, textWidth)
    doc.text(parts, size.margin, y)
    y += parts.length * size.info * 0.42
  }

  line('Преподаватель', fio)
  line('Дисциплины', (courses || []).join(', '))
  line('Учебные планы', (plans || []).join(', '))
  line('Учебные группы', (groups || []).join(', '))

  const { columns, rows } = buildGrid(lessons)

  doc.autoTable({
    head: buildHead(columns, size),
    body: buildBody(rows, columns),
    startY: y + size.info * 0.5,
    margin: size.margin,
    // содержимое ячейки переносится, а не обрезается, и таблица умещается
    // по ширине листа
    tableWidth: 'auto',
    styles: {
      font: FONT,
      fontSize: size.table,
      cellPadding: size.padding,
      overflow: 'linebreak',
      lineWidth: 0.2,
      lineColor: 120,
      textColor: 0,
      valign: 'middle',
      halign: 'center',
    },
    headStyles: { font: FONT, fontStyle: 'bold', fillColor: false, textColor: 0 },
    columnStyles: {
      0: { cellWidth: size.dayWidth, halign: 'left', fontStyle: 'bold' },
      1: { cellWidth: size.pairWidth },
    },
  })

  return doc
}

export const STATEMENT_FILE_NAME = 'Выписка из расписания по преподавателю.pdf'
